// User-facing copy for API failures. The API's problem+json `detail` is written
// for developers as often as for people, and some of it names internal enum
// values ("Cannot transition buyer funnel from \"cim\" to …"). Everything a
// user sees goes through here: known messages are rewritten in plain words,
// any leftover quoted snake_case identifier is humanized, and failures with no
// usable detail (non-JSON 502/524 pages, network drops, 5xx) get a friendly
// sentence. The original text stays on ApiError.problem / ApiError.debug for
// debugging and is never displayed.
import { FUNNEL_STAGE_LABELS } from './access';
import type { FunnelStage, ProblemDetails } from './types';

export const NETWORK_ERROR_MESSAGE =
  "We couldn't reach ClosingVault. Check your connection and try again.";
export const SERVER_ERROR_MESSAGE =
  'ClosingVault is having trouble right now. Please try again in a moment.';
export const UNEXPECTED_ERROR_MESSAGE = 'Something went wrong. Please try again.';
/** The browser could not read a picked file off the person's own computer (moved,
 * renamed, a cloud-only OneDrive/Dropbox placeholder, an unplugged drive, a path
 * too long for Windows) — nothing reached ClosingVault, so "try again" alone
 * would just fail again. */
export const FILE_READ_ERROR_MESSAGE =
  "We couldn't open this file on your computer. If it's in OneDrive, Dropbox or a network folder, make sure it's downloaded (or copy it to your desktop), then add it again.";

/** A local file read failure (File.arrayBuffer / FileReader): a DOMException
 * named NotFoundError, NotReadableError or SecurityError. */
export function isFileReadError(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return (
    typeof DOMException !== 'undefined' &&
    error instanceof DOMException &&
    (name === 'NotFoundError' || name === 'NotReadableError' || name === 'SecurityError')
  );
}

const LIFECYCLE_LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  archived: 'Archived',
  closed: 'Closed',
  trashed: 'Trash',
};

const ENVELOPE_STATUS_LABELS: Record<string, string> = {
  created: 'waiting for consent',
  consent_recorded: 'consented',
  signed: 'signed',
  sealed: 'sealed',
};

function stage(s: string | undefined): string {
  return FUNNEL_STAGE_LABELS[s as FunnelStage] ?? humanizeIdentifier(s ?? '');
}

/** 'loi_submitted' → 'loi submitted' (last resort for an unmapped identifier). */
function humanizeIdentifier(id: string): string {
  return id.replace(/_/g, ' ');
}

const KNOWN: { pattern: RegExp; message: (m: RegExpExecArray) => string }[] = [
  {
    pattern: /^Cannot transition buyer funnel from "(\w+)" to "(\w+)"/,
    message: (m) =>
      `This buyer can't move from ${stage(m[1])} to ${stage(m[2])}: stages advance one ` +
      'step at a time. Refresh to see the buyer’s current stage.',
  },
  {
    pattern: /^Cannot move buyer funnel back from "(\w+)"/,
    message: () => 'This buyer is already at the first stage (Teaser).',
  },
  {
    pattern: /^Cannot transition room lifecycle from "(\w+)" to "(\w+)"/,
    message: (m) =>
      `A room that is ${LIFECYCLE_LABELS[m[1] ?? ''] ?? humanizeIdentifier(m[1] ?? '')} ` +
      `can't be moved to ${LIFECYCLE_LABELS[m[2] ?? ''] ?? humanizeIdentifier(m[2] ?? '')}.`,
  },
  {
    pattern: /^Cannot (\w+) an envelope in status "(\w+)"/,
    message: (m) =>
      `This signing step is out of date: the agreement is already ` +
      `${ENVELOPE_STATUS_LABELS[m[2] ?? ''] ?? humanizeIdentifier(m[2] ?? '')}. ` +
      'Refresh and continue from the current step.',
  },
  {
    pattern: /^Not a category tag in this room:/,
    message: () =>
      'One of the chosen categories no longer exists in this room. Refresh and try again.',
  },
];

/** Rewrite one API `detail` for display (see module comment). */
export function humanizeProblemDetail(detail: string): string {
  for (const k of KNOWN) {
    const m = k.pattern.exec(detail);
    if (m) return k.message(m);
  }
  // Any remaining quoted internal identifier ("loi_submitted") → plain words.
  return detail.replace(/"([a-z]+(?:_[a-z]+)+)"/g, (_, id: string) => humanizeIdentifier(id));
}

/** The API is LOCKED (ADR-0069): its keys need the company recovery kit. */
export const KEYS_LOCKED_MESSAGE =
  'ClosingVault is locked. A platform owner can unlock it at /unlock.';

/**
 * A 429: the API's own words when it wrote them for people (a problem with a
 * stable `code`, e.g. "Too many codes have been sent in the last hour"), else
 * how long to wait when the server said (Retry-After), else a plain sentence.
 * The rate limiter's own text ("Rate limit exceeded…") is never shown.
 */
export function tooManyTriesMessage(
  problem: ProblemDetails | undefined,
  retryAfterSeconds?: number,
): string {
  if (problem?.code && problem.detail) return humanizeProblemDetail(problem.detail);
  if (typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0) {
    const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
    return `Too many tries. Wait ${minutes} minute${minutes === 1 ? '' : 's'}, then try again.`;
  }
  return 'Too many attempts. Please wait a moment and try again.';
}

/** The message to show for a failed response with this status / problem body. */
export function messageForFailure(
  status: number,
  problem: ProblemDetails | undefined,
  retryAfterSeconds?: number,
): string {
  if (status === 0) return NETWORK_ERROR_MESSAGE;
  if (status === 429) return tooManyTriesMessage(problem, retryAfterSeconds);
  if (status === 503 && problem?.code === 'keys_locked') return KEYS_LOCKED_MESSAGE;
  // 5xx (incl. Cloudflare 52x): the detail, if any, is not for users.
  if (status >= 500) return SERVER_ERROR_MESSAGE;
  if (problem?.code === 'section_items_due') {
    // Every item counts, required or optional (ADR-0032); an older server sent
    // only the required count.
    const n = problem.unresolved ?? problem.requiredDue;
    if (typeof n === 'number') {
      return (
        `${n} item${n === 1 ? ' is' : 's are'} still unresolved (optional items count too) — ` +
        `upload ${n === 1 ? 'it' : 'them'} or mark ${n === 1 ? 'it' : 'them'} “Doesn’t apply to ` +
        'my company” before certifying complete.'
      );
    }
  }
  const raw = problem?.detail ?? problem?.title;
  if (raw) return humanizeProblemDetail(raw);
  if (status === 401) return 'Your session has ended. Please sign in again.';
  if (status === 403) return "You don't have access to do that.";
  if (status === 404) return "We couldn't find that. It may have been removed.";
  return UNEXPECTED_ERROR_MESSAGE;
}

/** The upload screens' notice for files whose exact bytes the room already
 * held — the API skipped them (ADR-0028). Empty when there are none. */
export function skippedUploadsNotice(count: number): string {
  if (count <= 0) return '';
  return count === 1
    ? '1 file was already in this room and was skipped.'
    : `${count} files were already in this room and were skipped.`;
}

/** A document's bytes could not be read from storage right now (the API answers
 * 503 for a storage read failure; 404 only for a missing object). */
export const DOCUMENT_UNAVAILABLE_MESSAGE = 'This document is temporarily unavailable — try again.';

/** A buyer opened a document whose redaction is not finished (ADR-0064, 409). */
export const DOCUMENT_BEING_PREPARED_MESSAGE =
  'This document is being prepared. It will open here once it is ready.';

/** The message for a failed document-content read (the viewers fetch raw bytes,
 * outside the JSON client). `notFound` is the surface's own 403/404 wording. */
export function documentReadFailureMessage(status: number, notFound: string): string {
  if (status === 401) return 'Your session has ended. Please sign in again.';
  if (status === 403 || status === 404) return notFound;
  if (status === 409) return DOCUMENT_BEING_PREPARED_MESSAGE;
  if (status === 503) return DOCUMENT_UNAVAILABLE_MESSAGE;
  if (status >= 500) return SERVER_ERROR_MESSAGE;
  return UNEXPECTED_ERROR_MESSAGE;
}
